<script setup lang="ts">
/**
 * Secure your account (Figma `3606:68842`).
 *
 * Opened from "This wasn't me" (email 05) or "Secure my account" (email 03).
 * The same form as Set new password. After submit the email goes back to the
 * previous address and every session is signed out — this one included, so the
 * success screen ends on Log in. An expired or used link opens Link expired.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useAuthModal, useAuthStore } from '@/features/auth/store'
import { isPasswordValid } from '@/features/auth/model/passwordRules'
import { useAuthFormValidation } from '@/features/auth/model/useAuthFormValidation'
import AuthField from '@/features/auth/ui/parts/AuthField.vue'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import PasswordRequirementChips from '@/features/auth/ui/parts/PasswordRequirementChips.vue'
import { EmailLinkError, secureAccount } from '../api/emailLinks'
import { useEmailLinkStore } from '../store'

const { t } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()
const authStore = useAuthStore()
const links = useEmailLinkStore()

const password = ref('')
const passwordFocused = ref(false)

const clientValid = computed(() => isPasswordValid(password.value) && links.secureToken !== '')
const form = useAuthFormValidation({ clientValid })

const chipsVisible = computed(() =>
  passwordFocused.value || (password.value !== '' && !isPasswordValid(password.value)),
)

async function handleSubmit() {
  await form.submit(async () => {
    try {
      await secureAccount(links.secureToken, password.value)
      links.secureToken = ''
      authStore.logout()
      authModal.showAuthModal('accountSecured')
    } catch (error) {
      if (error instanceof EmailLinkError && error.reason !== 'unknown') {
        links.secureToken = ''
        authModal.showAuthModal('linkExpired')
        return
      }
      toast.show({ message: t('email_links.errors.generic'), tone: 'warning' })
    }
  })
}
</script>

<template>
  <AuthScreenShell
    :title="t('email_links.secure.title')"
    :description="t('email_links.secure.description')"
  >
    <form
      class="fair-auth-form"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div class="fair-auth-form__field">
        <AuthField
          v-model="password"
          input-id="auth-secure-password"
          type="password"
          autocomplete="new-password"
          :label="t('auth.set_password.password_label')"
          @focus="passwordFocused = true"
          @blur="passwordFocused = false"
        />
        <PasswordRequirementChips
          :password="password"
          :visible="chipsVisible"
        />
      </div>

      <FairButton
        class="fair-auth-cta"
        size="auto"
        type="submit"
        :label="t('auth.set_password.submit')"
        :disabled="form.submitDisabled.value"
      />
    </form>
  </AuthScreenShell>
</template>
